import {
  type AssetManifest,
  type EquipmentDropWire,
  QUANTUM_STABILIZER_ID,
} from '@kenocyber/shared';
import { useEffect } from 'react';
import { staticAssetUrl } from '../game/assets';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * Announces a component or a Quantum Stabiliser the SERVER confirmed.
 *
 * It appears only when a checkpoint answers with one - never when the client
 * thinks it killed something. The browser has no loot seed, cannot roll, and
 * would have nothing to announce. This presents the authoritative answer and
 * decides nothing.
 *
 * ## It must not interrupt the fight
 *
 * Floating and non-interactive: it sits over the arena like every other panel,
 * so it never resizes or recreates the Phaser canvas. `pointer-events: none` on
 * the stack with the close button opted back in is what keeps shots, the HUD
 * and the inventory button reachable underneath it - a toast that swallowed a
 * click during a boss would be worse than no toast.
 *
 * Nothing here touches the session: no bootstrap, no snapshot, no sequence, no
 * equip. The item is already in the vault by the time this renders.
 */

interface EquipmentDropToastProps {
  readonly drops: readonly EquipmentDropWire[];
  readonly manifest: AssetManifest;
  readonly onDismiss: (dropId: string) => void;
}

/** Long enough to read the name, the rarity and the Power. */
const VISIBLE_MS = 5_000;

function Toast({
  drop,
  manifest,
  onDismiss,
}: {
  drop: EquipmentDropWire;
  manifest: AssetManifest;
  onDismiss: (dropId: string) => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(drop.dropId), VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [drop.dropId, onDismiss]);

  const stabilizer = drop.kind === 'stabilizer';
  /*
   * The component's own PNG, at the rarity that was rolled.
   *
   * `definitionId` already encodes both, and it is the same key the inventory
   * cell resolves, so the toast and the cell cannot disagree about which art
   * belongs to which roll. No new asset is generated for this.
   */
  const url = staticAssetUrl(manifest, stabilizer ? QUANTUM_STABILIZER_ID : drop.definitionId);

  return (
    <div
      className="equip-drop-toast"
      /*
       * The rarity presentation the whole game already uses, from the same
       * catalog: Anomaly burns yellow into red and Prismatic is holographic
       * because `rarityPaint` says so, not because this file has a second
       * colour table.
       */
      data-kind={drop.kind}
      data-rarity={drop.rarity ?? undefined}
      data-paint={drop.rarity ? rarityPaint(drop.rarity) : undefined}
      data-testid={`equip-drop-toast-${drop.dropId}`}
      style={drop.rarity ? rarityStyle(drop.rarity) : undefined}
      role="status"
    >
      <span className="equip-drop-toast-art">
        {url ? (
          <img src={url} alt="" draggable={false} data-testid="equip-drop-toast-art" />
        ) : (
          <span aria-hidden="true">?</span>
        )}
      </span>
      <span className="equip-drop-toast-text">
        <strong className="equip-drop-toast-title" data-testid="equip-drop-toast-title">
          {stabilizer ? 'ESTABILIZADOR QUÂNTICO OBTIDO' : 'COMPONENTE OBTIDO'}
        </strong>
        <span data-testid="equip-drop-toast-name">{drop.name}</span>
        {stabilizer ? (
          // No Power and no rarity: a stabiliser has neither, and printing a
          // placeholder for something that does not exist is worse than silence.
          <span className="equip-drop-toast-quantity" data-testid="equip-drop-toast-quantity">
            ×1
          </span>
        ) : (
          <span className="equip-drop-toast-stats">
            <span data-testid="equip-drop-toast-rarity">
              {drop.rarity ? rarityLabel(drop.rarity) : ''}
            </span>
            <span data-testid="equip-drop-toast-power">
              Power <span className="equip-power-value">{drop.power}</span>
            </span>
          </span>
        )}
        {drop.fromBoss ? (
          <span className="equip-drop-toast-source">Derrubado por um boss</span>
        ) : null}
      </span>
      <button
        type="button"
        className="equip-drop-toast-close"
        data-testid="equip-drop-toast-close"
        aria-label={`Fechar aviso de ${drop.name}`}
        onClick={() => onDismiss(drop.dropId)}
      >
        ×
      </button>
    </div>
  );
}

/**
 * How many announcements are on screen at once.
 *
 * A checkpoint can confirm more than one drop - two kills in the same window,
 * rarely - and none of them may be grouped, hidden or discarded. They queue
 * instead: the first few show, the rest wait, and dismissing one lets the next
 * through immediately. Three is what fits beside the HUD without covering the
 * arena.
 */
const VISIBLE_AT_ONCE = 3;

export function EquipmentDropToasts({ drops, manifest, onDismiss }: EquipmentDropToastProps) {
  if (drops.length === 0) {
    return null;
  }
  return (
    <div className="equip-drop-toasts" data-testid="equipment-drop-toasts" aria-live="polite">
      {drops.slice(0, VISIBLE_AT_ONCE).map((drop) => (
        // Keyed by the SERVER's drop id: stable, unique per account, derived
        // from the kill. Two answers carrying the same drop cannot render twice.
        <Toast key={drop.dropId} drop={drop} manifest={manifest} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
